import { useCallback, useMemo, useRef } from "react";
import { proxy, useSnapshot } from "valtio";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { usePreviewContext } from "../../../contexts/preview-context";
import { useAppContext } from "../../../hooks/useAppContext";
import { useEditor } from "../../../hooks/useEditor";
import { useFeatureFlagToggle } from "../../../hooks/useFeatureFlags";
import { transformPoint } from "../../../transform";
import { useGuidelineOverlay } from "../../guideline-overlay/use-guideline-overlay";
import { ZoneSelectionBoxHandlers } from "../../zone-selection-box";
import { TransformType, useSnapping } from "./use-snapping";
import { getAllowedZoneRegion, getDisallowedZoneRegion } from "./use-zone-move";

const isZoneOnRegion = (
	zoneAsFrame: eXpress.math.Frame | undefined,
	region: eXpress.math.Rect,
): boolean => {
	if (!zoneAsFrame) return false;
	// Check if a point of the zone is in the region
	// We cannot use bounding box (a corner of the bounding box does not contain anything to draw)
	// return zoneAsFrame.corners().some((corner) => region.containsPoint(corner));
	return zoneAsFrame.overlaps(region.toFrame());
};

const isValidZoneTransform = (
	zoneTransform: eXpress.core.IZoneTransform,
	allowedRegion: eXpress.math.Rect | undefined,
	disallowedRegion: eXpress.math.Rect | undefined,
): boolean => {
	const zoneAsFrame = zoneTransform.getParallelogram();
	if (allowedRegion && !isZoneOnRegion(zoneAsFrame, allowedRegion)) return false;
	return disallowedRegion ? !isZoneOnRegion(zoneAsFrame, disallowedRegion) : true;
};

/** Shared state */
const zoneResizeState = proxy({
	isZoneResizing: false,
});

/**
 * Logic to resize a zone
 * This intentionally doesn't include the logic to trigger it
 * Could be based on gestures, keyboard, ...
 */
export function useZoneResize() {
	/** The start location of dragging (In DIP so it is independent of scale panning) */
	const zoneResizeRef = useRef<
		| {
				selectedZone: eXpress.core.ITransformableZone;
				/** Start position of finger (in DIP) */
				touchStartInDip: eXpress.math.Point;
				/** The zone transform at the start */
				zoneTransform: eXpress.core.IZoneTransform;
				/** Remember our command row Id to know if we can finalize/undo it at the end */
				undoRowId: string | undefined;
				// The handle (corner/side) that is dragging
				handle: ZoneSelectionBoxHandlers;
				/** The region were we can place out zone */
				allowedRegion: eXpress.math.Rect | undefined;
				/** The region were no zone can be placed */
				disallowedRegion: eXpress.math.Rect | undefined;
		  }
		| undefined
	>();

	const { getScreenToSpreadTransform } = usePreviewContext();
	const editor = useEditor();
	const dashboard = useDashboardContext();
	const newZoneResize = useFeatureFlagToggle("newZoneResize");
	const { googleAnalytics } = useAppContext();

	const { isZoneResizing } = useSnapshot(zoneResizeState);

	const disposeDebugFrames = useMemo<(() => void)[]>(() => [], []);

	const snapTransform = useSnapping();
	const [lines, useLines] = useGuidelineOverlay();

	const start = useCallback(
		function startZoneResizing({
			selectedZone,
			pointOnScreen,
			handle,
		}: {
			selectedZone: eXpress.core.ITransformableZone;
			pointOnScreen: eXpress.math.Point;
			handle: ZoneSelectionBoxHandlers;
		}) {
			// Get DIP coordinate touched
			const screenToSpreadDip = getScreenToSpreadTransform(
				selectedZone.spread,
				undefined,
			)?.inverse();
			const touchStartInDip = screenToSpreadDip
				? transformPoint(pointOnScreen, screenToSpreadDip)
				: undefined;

			useLines.setIsZoneMoving(true);
			zoneResizeState.isZoneResizing = true;
			zoneResizeRef.current = touchStartInDip
				? {
						selectedZone,
						touchStartInDip,
						zoneTransform: selectedZone.transform(),
						undoRowId: undefined,
						handle,
						allowedRegion: getAllowedZoneRegion(selectedZone),
						disallowedRegion: getDisallowedZoneRegion(selectedZone),
				  }
				: undefined;
		},
		[getScreenToSpreadTransform, useLines],
	);

	const update = useCallback(
		function zoneResizing(pointOnScreen: eXpress.math.Point) {
			const {
				touchStartInDip,
				zoneTransform,
				selectedZone,
				handle,
				allowedRegion,
				disallowedRegion,
			} = zoneResizeRef.current || {};
			if (!touchStartInDip || !zoneTransform || !selectedZone || !handle) return;

			// Get DIP Coordinate touched
			const screenToSpreadDip = getScreenToSpreadTransform(
				selectedZone.spread,
				undefined,
			)?.inverse();
			const currentDip = screenToSpreadDip
				? transformPoint(pointOnScreen, screenToSpreadDip)
				: undefined;
			if (!currentDip) return;

			const minSizeInDip = selectedZone.definition?.getMinZoneEdgeLength() ?? 1;

			/** Pointer displacement relative to start position in DIP */
			const displacementInSpreadSpace = currentDip.subPoint(touchStartInDip); // The displacement between original and current position
			const spreadToNonRotateZone = eXpress.math.rotateDeg(zoneTransform.angle);
			const displacementInZoneSpace = displacementInSpreadSpace.div(spreadToNonRotateZone); // When zone is rotate we need to convert the displacement to the zone space

			const zoneRect = eXpress.math.rect(zoneTransform);
			let newFrame: eXpress.math.Frame | undefined;

			/** Show a rect with a frame applied to it on screen */
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			function debugFrame(
				id: string,
				rect: eXpress.math.Rect,
				frame: eXpress.math.Frame,
				color = "blue",
			) {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const parentEl = document.querySelector(".app")!;
				let frameEl = parentEl.querySelector(`frame-${id}`);
				if (!frameEl) {
					parentEl?.insertAdjacentHTML(
						"beforeend",
						`<svg id="frame-${id}" class="frame" style="position: absolute; pointer-events: none" width="100%" height="100%" >
						<circle cx="50" cy="50" r="3" fill="${color}" />
						<path class="frame__main" fill="transparent" stroke="${color}" style="stroke-width: 2"/>
						<path class="frame__close"fill="transparent" stroke="${color}" style="stroke-width: 0.3"/>
					</svg>`,
					);
					frameEl = document.getElementById(`frame-${id}`);
					if (frameEl) disposeDebugFrames.push(() => frameEl?.remove()); // add cleanup function
				}

				if (!frameEl) return;
				const pathEl1 = frameEl.querySelector<SVGClipPathElement>(".frame__main");
				const pathEl2 = frameEl.querySelector<SVGClipPathElement>(".frame__close");
				const originEl = frameEl.querySelector<SVGClipPathElement>("circle");
				if (!pathEl1 || !pathEl2 || !originEl || !screenToSpreadDip) return;

				// Draw frame as parallellogram
				const cornersInDip = eXpress.math.Frame.fromRect(rect).mul(frame).corners();
				const cornersOnScreen = cornersInDip.map((p) =>
					transformPoint(p, screenToSpreadDip.inverse()),
				);
				const [tl, tr, bl, br] = cornersOnScreen;

				const path = `
					M${tr.left} ${tr.top}
					L${tl.left} ${tl.top}
					L${bl.left} ${bl.top}
				`;
				pathEl1.setAttribute("d", path);

				const path2 = `
					M${tr.left} ${tr.top}
					L${br.left} ${br.top}
					L${bl.left} ${bl.top}
				`;
				pathEl2.setAttribute("d", path2);

				// Update dot of origin
				const origin = transformPoint(eXpress.math.Point.zero, screenToSpreadDip.inverse());
				originEl.setAttribute("cx", `${origin.left}px`);
				originEl.setAttribute("cy", `${origin.top}px`);
			}

			/** Calculate a frame that brings us from and identity frame to the new target zone */
			function calcNewFrame(options: {
				displacementInZoneSpace: eXpress.math.Vector;
				resizeOrigin: eXpress.math.Point;
				scaleUniform: boolean;
			}) {
				// eslint-disable-next-line @typescript-eslint/no-shadow
				let { displacementInZoneSpace } = options;
				const { resizeOrigin, scaleUniform } = options;
				if (!zoneTransform) return eXpress.math.Frame.identity;

				// Check if a side reaches the minimum size
				displacementInZoneSpace = eXpress.math.vector(
					Math.max(displacementInZoneSpace.width, minSizeInDip - zoneTransform.width),
					Math.max(displacementInZoneSpace.height, minSizeInDip - zoneTransform.height),
				);

				// Make displacement uniform
				if (scaleUniform) {
					const zoneAspectRatio = zoneRect.getAspectRatio();
					const displacementRatio = displacementInZoneSpace.getAspectRatio();

					if (displacementRatio > 1) {
						displacementInZoneSpace = eXpress.math.vector(
							displacementInZoneSpace.width,
							displacementInZoneSpace.width / zoneAspectRatio,
						);
					} else {
						displacementInZoneSpace = eXpress.math.vector(
							displacementInZoneSpace.height * zoneAspectRatio,
							displacementInZoneSpace.height,
						);
					}
				}

				const pivotPoint = eXpress.math.point(zoneTransform.left, zoneTransform.top); // All our zones rotate on the topLeft zone corner

				// Remark: At DC the selection box and scaling is around the border
				const frame = zoneRect
					.toFrame()
					.scale(
						(zoneTransform.width + displacementInZoneSpace.width) / zoneTransform.width,
						(zoneTransform.height + displacementInZoneSpace.height) /
							zoneTransform.height,
						resizeOrigin,
					)
					.rotateDeg(zoneTransform.angle, pivotPoint); // perform the zone rotation

				return frame;
			}

			// Apply the zone rotation
			// Adjust dx, dy to:
			// - make positive for growing, negative for shrinking
			// - let corners scale uniform
			const zoneAspectRatio = zoneRect.getAspectRatio();

			switch (handle) {
				case ZoneSelectionBoxHandlers.TopLeft: {
					// Scale so incrementing values grow zone (only count direction of longest edge)
					const directionScale =
						zoneAspectRatio > 1
							? eXpress.math.vector(-1, 0)
							: eXpress.math.vector(0, -1);
					newFrame = calcNewFrame({
						displacementInZoneSpace: displacementInZoneSpace
							.mul(directionScale)
							.toVector(),
						resizeOrigin: eXpress.math.point(zoneRect.right, zoneRect.bottom),
						scaleUniform: true,
					});
					break;
				}
				case ZoneSelectionBoxHandlers.Top: {
					const directionScale = eXpress.math.vector(0, -1); // Scale so incrementing values grow zone
					newFrame = calcNewFrame({
						displacementInZoneSpace: displacementInZoneSpace
							.mul(directionScale)
							.toVector(),
						resizeOrigin: eXpress.math.point(zoneRect.left, zoneRect.bottom),
						scaleUniform: false,
					});
					break;
				}
				case ZoneSelectionBoxHandlers.TopRight: {
					// Scale so incrementing values grow zone (only count direction of longest edge)
					const directionScale =
						zoneAspectRatio > 1
							? eXpress.math.vector(1, 0)
							: eXpress.math.vector(0, -1);
					newFrame = calcNewFrame({
						displacementInZoneSpace: displacementInZoneSpace
							.mul(directionScale)
							.toVector(),
						resizeOrigin: eXpress.math.point(zoneRect.left, zoneRect.bottom),
						scaleUniform: true,
					});
					break;
				}
				case ZoneSelectionBoxHandlers.Left: {
					const directionScale = eXpress.math.vector(-1, 0); // Scale so incrementing values grow zone
					newFrame = calcNewFrame({
						displacementInZoneSpace: displacementInZoneSpace
							.mul(directionScale)
							.toVector(),
						resizeOrigin: eXpress.math.point(zoneRect.right, zoneRect.top),
						scaleUniform: false,
					});
					break;
				}
				case ZoneSelectionBoxHandlers.Right: {
					const directionScale = eXpress.math.vector(1, 0); // Scale so incrementing values grow zone
					newFrame = calcNewFrame({
						displacementInZoneSpace: displacementInZoneSpace
							.mul(directionScale)
							.toVector(),
						resizeOrigin: eXpress.math.point(zoneRect.left, zoneRect.top),
						scaleUniform: false,
					});
					break;
				}
				case ZoneSelectionBoxHandlers.BottomLeft: {
					// Scale so incrementing values grow zone (only count direction of longest edge)
					const directionScale =
						zoneAspectRatio > 1
							? eXpress.math.vector(-1, 0)
							: eXpress.math.vector(0, 1);
					newFrame = calcNewFrame({
						displacementInZoneSpace: displacementInZoneSpace
							.mul(directionScale)
							.toVector(),
						resizeOrigin: eXpress.math.point(zoneRect.right, zoneRect.top),
						scaleUniform: true,
					});
					break;
				}
				case ZoneSelectionBoxHandlers.Bottom: {
					const directionScale = eXpress.math.vector(0, 1); // Scale so incrementing values grow zone
					newFrame = calcNewFrame({
						displacementInZoneSpace: displacementInZoneSpace
							.mul(directionScale)
							.toVector(),
						resizeOrigin: eXpress.math.point(zoneRect.right, zoneRect.top),
						scaleUniform: false,
					});
					break;
				}
				case ZoneSelectionBoxHandlers.BottomRight: {
					// Scale so incrementing values grow zone (only count direction of longest edge)
					const directionScale =
						zoneAspectRatio > 1 ? eXpress.math.vector(1, 0) : eXpress.math.vector(0, 1);
					newFrame = calcNewFrame({
						displacementInZoneSpace: displacementInZoneSpace
							.mul(directionScale)
							.toVector(),
						resizeOrigin: eXpress.math.point(zoneRect.left, zoneRect.top),
						scaleUniform: true,
					});
					break;
				}
				default: {
					newFrame = eXpress.math.Frame.identity;
				}
			}

			// Get new ZoneTransform with a new size for the command
			let newZoneTransform = zoneTransform.clone() as eXpress.core.ZoneTransform;
			const newZoneRect = newFrame.toRect();
			newZoneTransform.left = newZoneRect.left;
			newZoneTransform.top = newZoneRect.top;
			newZoneTransform.width = newZoneRect.width;
			newZoneTransform.height = newZoneRect.height;

			// Check if zone is still on the allowed region (page)
			// Currently, this does not 'fix' the incorrect size but skips this last invalid position
			// This means a zone:
			// - cannot be moved from a disallowed zone to an allowed zone
			// - will not always be at the DIP limit of the maximum/minimum size
			if (!isValidZoneTransform(newZoneTransform, allowedRegion, disallowedRegion)) return;

			if (snapTransform) {
				const newTransform = snapTransform(
					newZoneTransform,
					lines,
					TransformType.Resize,
					handle,
					selectedZone,
				);

				if (!newTransform.isSimilarTo(newZoneTransform)) {
					newZoneTransform.left = newTransform.left;
					newZoneTransform.top = newTransform.top;
					newZoneTransform.height = newTransform.height;
					newZoneTransform.width = newTransform.width;
				}
			}
			newZoneTransform = newZoneTransform.snapToDips();

			// Don't snap if it makes the zone smaller than allowed
			if (newZoneTransform.width < minSizeInDip || newZoneTransform.height < minSizeInDip) {
				return;
			}

			// Create the command to execute
			const command = dashboard.createResizeCommand(
				selectedZone,
				newZoneTransform,
				true,
				newZoneResize,
			);

			// When dragging, we can receive events faster than the screen can show (faster than refresh rate of the screen)
			// I tested with RAF throttling but that caused a noticable delay when dragging, did not use RAF throttling
			editor.commandManager.exec(command);

			useLines.setIsZoneMoving(true);

			if (zoneResizeRef.current)
				zoneResizeRef.current.undoRowId = editor.commandManager.getRowId();
		},
		[
			getScreenToSpreadTransform,
			snapTransform,
			dashboard,
			editor.commandManager,
			disposeDebugFrames,
			lines,
			useLines,
			newZoneResize,
		],
	);

	const end = useCallback(
		function endZoneResizing() {
			disposeDebugFrames.forEach((d) => d());
			if (zoneResizeRef.current?.undoRowId === editor.commandManager.getRowId()) {
				editor.commandManager.makeLastNonInteractive();
				googleAnalytics.trackEventToGa({
					trackingAction: "TextZoneScaled",
				});
			}
			useLines.setIsZoneMoving(false);
			zoneResizeState.isZoneResizing = false;
			zoneResizeRef.current = undefined;
		},
		[disposeDebugFrames, editor.commandManager, googleAnalytics, useLines],
	);

	const cancel = useCallback(
		function cancelZoneResing() {
			disposeDebugFrames.forEach((d) => d());
			// Undo if last command is our resize
			if (zoneResizeRef.current?.undoRowId === editor.commandManager.getRowId()) {
				editor.commandManager.undo(true);
			}
			zoneResizeState.isZoneResizing = false;
			zoneResizeRef.current = undefined;
		},
		[disposeDebugFrames, editor.commandManager],
	);

	return [
		isZoneResizing,
		useMemo(
			// Try to return a stable object
			() => ({
				start,
				update,
				end,
				cancel,
			}),
			[start, update, end, cancel],
		),
	] as const;
}
